import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    getCellKey,
    getMonthGrid,
    groupEventsByDay,
    resolveViewerCalendarRangeKeys,
} from '../utils/eventsCalendar';

const DEFAULT_REFRESH_INTERVAL_MS = 30 * 1000;

function toMonthStart(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

function parseTime(value) {
    if (!value) {
        return NaN;
    }
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : NaN;
}

function parseMonthStartFromCalendarKey(key) {
    if (typeof key !== 'string') {
        return null;
    }

    const [year, month] = key.split('-').map((value) => Number.parseInt(value, 10));
    if (!Number.isFinite(year) || !Number.isFinite(month)) {
        return null;
    }

    return new Date(year, month - 1, 1, 0, 0, 0, 0);
}

function sameMonth(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function toMonthPrefix(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function toValidDateOrNull(value) {
    return value instanceof Date && !Number.isNaN(value.getTime()) ? value : null;
}

export function useRefreshingCurrentTime(today, refreshIntervalMs = DEFAULT_REFRESH_INTERVAL_MS) {
    const externalToday = toValidDateOrNull(today);
    const externalTodayMs = externalToday ? externalToday.getTime() : null;
    const [internalToday, setInternalToday] = useState(() => new Date());

    useEffect(() => {
        if (externalTodayMs !== null) {
            return undefined;
        }

        setInternalToday(new Date());

        if (!Number.isFinite(refreshIntervalMs) || refreshIntervalMs <= 0) {
            return undefined;
        }

        const intervalId = setInterval(() => {
            setInternalToday(new Date());
        }, refreshIntervalMs);

        return () => {
            clearInterval(intervalId);
        };
    }, [externalTodayMs, refreshIntervalMs]);

    return useMemo(() => (
        externalTodayMs !== null ? new Date(externalTodayMs) : internalToday
    ), [externalTodayMs, internalToday]);
}

function resolveEventCalendarRange(event) {
    return resolveViewerCalendarRangeKeys(event);
}

function buildVisibleEventSignature(events) {
    if (!Array.isArray(events) || events.length === 0) {
        return '';
    }

    return events
        .map((event) => {
            const { startKey, endKey } = resolveEventCalendarRange(event);
            if (!startKey && !endKey) {
                return '';
            }
            return `${String(event?.id || '')}:${startKey}:${endKey}`;
        })
        .filter(Boolean)
        .sort()
        .join('|');
}

function resolveAnchorDistance(event, referenceMs) {
    const start = parseTime(event?.startAt);
    const end = parseTime(event?.endAt);
    const effectiveEnd = Number.isNaN(end) ? start : end;

    if (!Number.isNaN(start) && !Number.isNaN(effectiveEnd) && start <= referenceMs && effectiveEnd >= referenceMs) {
        return 0;
    }
    if (!Number.isNaN(effectiveEnd) && effectiveEnd < referenceMs) {
        return referenceMs - effectiveEnd;
    }
    if (!Number.isNaN(start) && start > referenceMs) {
        return start - referenceMs;
    }
    if (!Number.isNaN(start)) {
        return Math.abs(start - referenceMs);
    }
    if (!Number.isNaN(effectiveEnd)) {
        return Math.abs(effectiveEnd - referenceMs);
    }
    return Number.POSITIVE_INFINITY;
}

function resolveEventMonth(event, referenceDate) {
    const { startKey, endKey } = resolveEventCalendarRange(event);
    const reference = toValidDateOrNull(referenceDate);
    const referenceKey = reference ? getCellKey(reference) : '';

    if (referenceKey && startKey && endKey && startKey <= referenceKey && referenceKey <= endKey) {
        const activeMonth = parseMonthStartFromCalendarKey(referenceKey);
        if (activeMonth) {
            return activeMonth;
        }
    }

    const startMonth = parseMonthStartFromCalendarKey(startKey);
    if (startMonth) {
        return startMonth;
    }

    return parseMonthStartFromCalendarKey(endKey);
}

function eventIsVisibleInMonth(event, monthDate) {
    const visibleMonth = toValidDateOrNull(monthDate);
    if (!visibleMonth) {
        return false;
    }

    const { startKey, endKey } = resolveEventCalendarRange(event);
    if (!startKey || !endKey) {
        return false;
    }

    const monthPrefix = toMonthPrefix(visibleMonth);
    const startMonthPrefix = startKey.slice(0, 7);
    const endMonthPrefix = endKey.slice(0, 7);
    return startMonthPrefix <= monthPrefix && monthPrefix <= endMonthPrefix;
}

function resolveAnchorMonth(events, referenceDate) {
    if (!Array.isArray(events) || events.length === 0) {
        return null;
    }

    const referenceMs = referenceDate.getTime();
    let bestEvent = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    events.forEach((event) => {
        const monthStart = resolveEventMonth(event, referenceDate);
        if (!monthStart) {
            return;
        }

        const distance = resolveAnchorDistance(event, referenceMs);
        if (distance < bestDistance) {
            bestEvent = event;
            bestDistance = distance;
            return;
        }

        if (distance === bestDistance && bestEvent) {
            const start = parseTime(event?.startAt);
            const bestStart = parseTime(bestEvent?.startAt);
            if (!Number.isNaN(start) && (Number.isNaN(bestStart) || start < bestStart)) {
                bestEvent = event;
            }
        }
    });

    return bestEvent ? resolveEventMonth(bestEvent, referenceDate) : null;
}

/**
 * Returns the current calendar month descriptor plus navigation callbacks.
 * - `monthDate`: a Date pinned to the 1st of the month at 00:00 local time.
 * - `days`: 6×7 matrix of { date, isCurrentMonth, isToday }.
 * - `eventsByDay`: Map<YYYY-MM-DD, Event[]> bucketed by the viewer-local
 *   day keys used by the month grid cells.
 */
export default function useEventsCalendarMonth(events, options = {}) {
    const {
        weekStartsOn = 1,
        initialMonth,
        today,
        refreshIntervalMs = DEFAULT_REFRESH_INTERVAL_MS,
    } = options;

    const resolvedInitialMonth = useMemo(() => (
        initialMonth instanceof Date && !Number.isNaN(initialMonth.getTime())
            ? toMonthStart(initialMonth)
            : null
    ), [initialMonth]);
    const resolvedInitialMonthTime = resolvedInitialMonth ? resolvedInitialMonth.getTime() : null;

    const [monthDate, setMonthDate] = useState(() => {
        const base = initialMonth instanceof Date ? initialMonth : new Date();
        return toMonthStart(base);
    });

    const effectiveToday = useRefreshingCurrentTime(today, refreshIntervalMs);
    const visibleEventSignature = useMemo(() => buildVisibleEventSignature(events), [events]);
    const lastAutoAnchorSignatureRef = useRef('');

    useEffect(() => {
        if (resolvedInitialMonthTime === null) {
            return;
        }
        setMonthDate((current) => (
            current.getTime() === resolvedInitialMonthTime
                ? current
                : new Date(resolvedInitialMonthTime)
        ));
    }, [resolvedInitialMonthTime]);

    useEffect(() => {
        if (!visibleEventSignature) {
            lastAutoAnchorSignatureRef.current = '';
            return;
        }

        if (visibleEventSignature === lastAutoAnchorSignatureRef.current) {
            return;
        }
        lastAutoAnchorSignatureRef.current = visibleEventSignature;

        const anchorMonth = resolveAnchorMonth(events, effectiveToday);
        if (!anchorMonth || sameMonth(anchorMonth, monthDate)) {
            return;
        }

        const monthHasVisibleEvents = events.some((event) => eventIsVisibleInMonth(event, monthDate));

        if (!monthHasVisibleEvents) {
            setMonthDate(anchorMonth);
        }
    }, [effectiveToday, events, monthDate, visibleEventSignature]);

    const days = useMemo(
        () => getMonthGrid(monthDate, weekStartsOn, effectiveToday),
        [effectiveToday, monthDate, weekStartsOn],
    );

    const eventsByDay = useMemo(() => groupEventsByDay(events), [events]);

    const goPrev = useCallback(() => {
        setMonthDate((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1));
    }, []);

    const goNext = useCallback(() => {
        setMonthDate((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1));
    }, []);

    const goToday = useCallback(() => {
        setMonthDate(toMonthStart(effectiveToday));
    }, [effectiveToday]);

    return {
        currentTime: effectiveToday,
        monthDate,
        days,
        eventsByDay,
        goPrev,
        goNext,
        goToday,
    };
}
